/**
 * Lightweight skeleton primitives.
 * Rules: no layout shift, no spinners, no heavy animations.
 * Uses tailwind's `animate-pulse` (single CSS keyframe, no JS).
 */
export function SkeletonLine({ w = "w-full", h = "h-3", className = "" }) {
  return <div className={`animate-pulse bg-zinc-100 rounded ${h} ${w} ${className}`} />;
}

export function SkeletonBlock({ h = "h-20", className = "" }) {
  return <div className={`animate-pulse bg-zinc-100 rounded-md ${h} ${className}`} />;
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-8 max-w-4xl" data-testid="dashboard-skeleton">
      <div className="space-y-2">
        <SkeletonLine w="w-40" h="h-3" />
        <SkeletonLine w="w-72" h="h-8" />
        <SkeletonLine w="w-56" h="h-3" />
      </div>
      <div className="bg-white border border-zinc-200 rounded-md">
        <div className="px-5 py-4 border-b border-zinc-100">
          <SkeletonLine w="w-48" h="h-4" />
        </div>
        <div className="divide-y divide-zinc-100">
          {[0, 1, 2].map((i) => (
            <div key={i} className="px-5 py-3.5 flex items-center gap-3">
              <SkeletonLine w="w-4" h="h-4" />
              <div className="flex-1 space-y-1.5">
                <SkeletonLine w="w-56" h="h-3.5" />
                <SkeletonLine w="w-40" h="h-2.5" />
              </div>
              <SkeletonLine w="w-16" h="h-4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AttendanceSkeleton() {
  return (
    <div className="space-y-6" data-testid="attendance-skeleton">
      <div className="space-y-2">
        <SkeletonLine w="w-40" h="h-8" />
        <SkeletonLine w="w-72" h="h-3" />
      </div>
      <div className="flex items-center gap-2">
        <SkeletonBlock h="h-10" className="w-10" />
        <SkeletonBlock h="h-11" className="w-56" />
        <SkeletonBlock h="h-10" className="w-10" />
      </div>
      <SkeletonBlock h="h-8" className="w-64" />
      <div className="border border-zinc-200 rounded-md bg-white divide-y divide-zinc-100">
        {[0, 1, 2].map((i) => (
          <div key={i} className="px-4 py-3.5 flex items-center gap-3">
            <SkeletonLine w="w-4" h="h-4" />
            <div className="flex-1 space-y-1.5">
              <SkeletonLine w="w-52" h="h-3.5" />
              <SkeletonLine w="w-36" h="h-2.5" />
            </div>
            <SkeletonLine w="w-20" h="h-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AthleteProfileSkeleton() {
  return (
    <div className="space-y-6 max-w-5xl" data-testid="athlete-profile-skeleton">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SkeletonLine w="w-32" h="h-4" />
        <div className="flex gap-2">
          <SkeletonBlock h="h-10" className="w-20" />
          <SkeletonBlock h="h-10" className="w-20" />
          <SkeletonBlock h="h-10" className="w-24" />
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md p-5">
        <div className="flex items-start gap-5">
          <div className="w-20 h-20 rounded-full bg-zinc-100 animate-pulse shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonLine w="w-56" h="h-7" />
            <div className="flex gap-1.5 pt-1">
              <SkeletonLine w="w-16" h="h-5" />
              <SkeletonLine w="w-20" h="h-5" />
              <SkeletonLine w="w-24" h="h-5" />
            </div>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <SkeletonBlock key={i} h="h-16" />)}
        </div>
      </div>
      <div className="flex gap-2">
        {[0, 1, 2, 3].map((i) => <SkeletonBlock key={i} h="h-9" className="w-24" />)}
      </div>
      <SkeletonBlock h="h-64" />
    </div>
  );
}
